import React, { useState } from 'react';
import { useAuth } from './AuthContext';
import AuthLayout from './AuthLayout';

export default function ResetPasswordScreen({ onSwitchToLogin }) {
  const { resetPassword } = useAuth();
  const [email, setEmail] = useState('');
  const [error, setError] = useState('');
  const [sent, setSent] = useState(false);
  const [pending, setPending] = useState(false);

  const submit = async () => {
    if (pending) return;
    setError('');
    setPending(true);
    const result = await resetPassword(email.trim());
    setPending(false);
    if (!result.ok) {
      setError(result.error || 'Could not send reset link.');
      return;
    }
    setSent(true);
  };

  const onKey = (e) => { if (e.key === 'Enter') submit(); };

  return (
    <AuthLayout
      eyebrow="Forgot your password?"
      heading={sent ? 'Check your inbox' : 'Reset your password'}
      subheading={sent
        ? `We've sent a recovery link to ${email}. Click it to set a new password.`
        : "Enter the email you signed up with and we'll send a recovery link."}
      footer={
        <button
          onClick={onSwitchToLogin}
          className="font-semibold text-amber-700 hover:text-amber-800 hover:underline"
        >
          ← Back to sign in
        </button>
      }
    >
      {sent ? (
        <div className="space-y-4">
          <div className="bg-amber-50 border border-amber-200 text-amber-900 rounded-lg px-4 py-4 text-sm">
            <div className="font-semibold mb-1">If an account exists for that email, the recovery link is on its way.</div>
            <div className="text-amber-800/80">Check your spam folder if it doesn't arrive within a couple of minutes.</div>
          </div>
          <button
            onClick={onSwitchToLogin}
            className="w-full bg-amber-500 hover:bg-amber-600 text-slate-900 font-bold py-3 rounded-lg shadow-sm transition"
          >
            Back to sign in
          </button>
        </div>
      ) : (
        <div className="space-y-4">
          <label className="block">
            <span className="text-xs uppercase tracking-wider font-semibold text-slate-600">Email</span>
            <input
              type="email"
              autoComplete="email"
              value={email}
              onChange={e => { setEmail(e.target.value); setError(''); }}
              onKeyDown={onKey}
              placeholder="you@example.com"
              autoFocus
              className="mt-1.5 w-full border border-slate-200 rounded-lg px-3 py-2.5 text-sm focus:outline-none focus:border-amber-500 focus:ring-2 focus:ring-amber-100 transition"
            />
          </label>
          {error && (
            <div className="bg-red-50 border border-red-200 text-red-700 rounded-lg px-3 py-2.5 text-sm flex items-start gap-2">
              <span aria-hidden="true">⚠</span>
              <span>{error}</span>
            </div>
          )}
          <button
            onClick={submit}
            disabled={!email.trim() || pending}
            className="w-full bg-amber-500 hover:bg-amber-600 active:bg-amber-700 disabled:opacity-50 disabled:cursor-not-allowed text-slate-900 font-bold py-3 rounded-lg shadow-sm transition"
          >
            {pending ? 'Sending…' : 'Send recovery link'}
          </button>
        </div>
      )}
    </AuthLayout>
  );
}
